import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Request,
  RequestType,
  requestTypeNavigateParser,
  Procedure,
} from "@interfaces";
import { api, queryClient } from "@contexts";
import { useMutation } from "@tanstack/react-query";
import { parseProcedureSuggestions } from "@screens/MedicalOrder/utils/parse";

interface RepeatRequestResponse {
  request: Request;
  sourceDocumentData: Record<string, any> | null;
}

function buildNavigationState(
  documentType: RequestType,
  sourceDocumentData: Record<string, any> | null,
): Record<string, any> {
  if (!sourceDocumentData) return {};

  switch (documentType) {
    case RequestType.COMPOUNDED_PRESCRIPTION:
      return sourceDocumentData;
    case RequestType.MEDICAL_CERTIFICATE:
      return {
        repeatedDocumentDetails: sourceDocumentData.documentDetails,
      };
    case RequestType.MEDICAL_ORDER:
      return {
        repeatedDiagnosticHypothesis: sourceDocumentData.diagnosticHypothesis,
        repeatedDocumentDetails: sourceDocumentData.documentDetails?.map(
          (detail: { description: string; code?: string }) => ({
            text: detail.code
              ? parseProcedureSuggestions({
                  description: detail.description,
                  code: detail.code,
                } as Procedure)
              : detail.description,
            code: detail.code ?? null,
          }),
        ),
        repeatedIndications: sourceDocumentData.indications,
      };
    default:
      return {};
  }
}

export const useRepeatRequest = () => {
  const navigate = useNavigate();
  const { requestId: sourceRequestId = "" } = useParams();

  const { mutateAsync: repeatRequest, isPending: isRepeatingDocument } =
    useMutation({
      mutationFn: (sourceRequestId: string) =>
        api<RepeatRequestResponse>({
          method: "POST",
          url: `/requests/${sourceRequestId}/repeat`,
        }),
      onSuccess: ({ request, sourceDocumentData }) => {
        const { id: newRequestId, documentType } = request;

        queryClient.invalidateQueries({
          queryKey: ["request", newRequestId],
        });

        const navigationState = buildNavigationState(
          documentType,
          sourceDocumentData,
        );

        navigate(
          `/app/solicitudes/${newRequestId}/${requestTypeNavigateParser[documentType]}/crear`,
          { state: navigationState },
        );
      },
      onError: (error) => {
        console.error("[useRepeatRequest] Error al repetir solicitud", {
          error,
          sourceRequestId,
        });
      },
    });

  const handleRepeatRequest = useCallback(() => {
    if (!sourceRequestId) return;
    repeatRequest(sourceRequestId);
  }, [sourceRequestId, repeatRequest]);

  return {
    handleRepeatRequest,
    isRepeatingDocument,
  };
};
